import { motion } from "framer-motion";
import { lazy, Suspense, useEffect, useState } from "react";
import StoreButtons from "@/components/landing/StoreButtons";

const Scene3D = lazy(() => import("@/components/landing/Scene3D"));

export default function Hero() {
    const [mountScene, setMountScene] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setMountScene(true), 80);
        return () => clearTimeout(t);
    }, []);

    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative min-h-[100svh] w-full overflow-hidden grain-bg aurora"
        >
            {/* Neon grid backdrop */}
            <div className="absolute inset-0 neon-grid z-0" aria-hidden="true" />

            {/* 3D scene — full background */}
            <div className="absolute inset-0 z-[1]" aria-hidden="true">
                {mountScene && (
                    <Suspense fallback={<div className="w-full h-full" />}>
                        <Scene3D />
                    </Suspense>
                )}
            </div>

            {/* Radial vignette overlay so text reads clearly */}
            <div
                className="absolute inset-0 z-[2] pointer-events-none"
                style={{
                    background:
                        "radial-gradient(ellipse at 28% 50%, rgba(10,10,12,0.82) 0%, rgba(10,10,12,0.55) 35%, rgba(10,10,12,0.2) 60%, transparent 80%)",
                }}
                aria-hidden="true"
            />

            {/* Content */}
            <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 pt-36 sm:pt-40 pb-20 min-h-[100svh] flex items-center">
                <div className="max-w-2xl">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#00F0FF]/25 bg-[#00F0FF]/5 text-[#00F0FF] text-xs font-medium tracking-wide"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] shadow-[0_0_8px_#00F0FF]" />
                        Rota · HGS · Yakıt · Molalar
                    </motion.div>

                    <motion.h1
                        initial="hidden"
                        animate="show"
                        variants={{
                            hidden: {},
                            show: { transition: { staggerChildren: 0.09, delayChildren: 0.15 } },
                        }}
                        data-testid="hero-headline"
                        className="font-display font-semibold mt-6 text-[44px] sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight"
                    >
                        {["Rota,", "maliyet", "ve"].map((w, i) => (
                            <motion.span
                                key={i}
                                variants={{
                                    hidden: { opacity: 0, y: 40, filter: "blur(12px)" },
                                    show: {
                                        opacity: 1,
                                        y: 0,
                                        filter: "blur(0px)",
                                        transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
                                    },
                                }}
                                className="inline-block mr-[0.25em]"
                            >
                                {w}
                            </motion.span>
                        ))}
                        <br />
                        {["molalar", "tek", "yerde."].map((w, i) => (
                            <motion.span
                                key={i}
                                variants={{
                                    hidden: { opacity: 0, y: 40, filter: "blur(12px)" },
                                    show: {
                                        opacity: 1,
                                        y: 0,
                                        filter: "blur(0px)",
                                        transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
                                    },
                                }}
                                className={`inline-block mr-[0.25em] ${i > 0 ? "shimmer-text" : ""}`}
                            >
                                {w}
                            </motion.span>
                        ))}
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-6 text-white/65 text-base sm:text-lg max-w-xl leading-relaxed"
                    >
                        A'dan B'ye en iyi rota, tahmini <span className="text-white">yakıt</span> ve <span className="text-white">HGS/otoyol</span> maliyeti,
                        güzergâh üzerindeki <span className="text-white">restoran, mescit, tuvalet ve oto yıkama</span> molaları — hepsi tek ekranda.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.34, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-9"
                    >
                        <StoreButtons />
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 0.6 }}
                        className="mt-10 flex items-center gap-6 text-xs text-white/45"
                    >
                        <div className="flex items-center gap-2">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 6 9 17l-5-5"/>
                            </svg>
                            iOS 16+
                        </div>
                        <div className="flex items-center gap-2">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 6 9 17l-5-5"/>
                            </svg>
                            Android 10+
                        </div>
                        <div className="flex items-center gap-2">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 6 9 17l-5-5"/>
                            </svg>
                            Ücretsiz
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Scroll hint */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2, duration: 1 }}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-white/40 text-[11px] tracking-[0.22em] uppercase flex flex-col items-center gap-2"
            >
                <span>Keşfet</span>
                <span className="w-px h-8 bg-gradient-to-b from-white/50 to-transparent" />
            </motion.div>
        </section>
    );
}
